// eslint-disable-next-line no-use-before-define
import React, { useState } from 'react';
import { Stack, Typography, CircularProgress } from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';

interface DeleteSiteButtonProps {
  siteId: number;
  onDelete: (siteId: number) => Promise<unknown>;
}

/**
 * Delete control for a single site. Owns its in-flight state so it can show a
 * spinner, block a second click, and stay keyboard-accessible while the delete
 * is running - preventing the double-click that caused Site.DoesNotExist
 * (Sentry UK-HORIZON-PROD-31A).
 */
const DeleteSiteButton = ({ siteId, onDelete }: DeleteSiteButtonProps): JSX.Element => {
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const handleDelete = (): void => {
    if (isDeleting) return;
    setIsDeleting(true);
    // finally clears the busy state on both success and failure; the parent's
    // onError surfaces the failure snackbar, so this never rejects.
    void onDelete(siteId).finally(() => setIsDeleting(false));
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      role="button"
      tabIndex={isDeleting ? -1 : 0}
      aria-disabled={isDeleting}
      aria-busy={isDeleting}
      data-testid={`delete-site-${siteId}`}
      sx={{
        cursor: isDeleting ? 'not-allowed' : 'pointer',
        padding: '8px 11px',
        pointerEvents: isDeleting ? 'none' : 'auto',
      }}
      gap={1}
      onClick={handleDelete}
      onKeyDown={(e): void => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleDelete();
        }
      }}>
      {isDeleting ? (
        <CircularProgress size={20} color="error" />
      ) : (
        <DeleteOutline color="error" sx={{ ':hover': { fillOpacity: 0.75 } }} />
      )}
      <Typography variant="subtitle1">{isDeleting ? 'Deleting…' : 'Delete'}</Typography>
    </Stack>
  );
};

export default DeleteSiteButton;
